(function () {
  'use strict';

  if (window.AxiomHeadingReveal) return;

  var HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6, [data-heading-reveal]';
  var WORD_CLASS = 'heading-reveal__word';
  var INNER_CLASS = 'heading-reveal__inner';
  var BASE_DELAY = 250;
  var WORD_STAGGER = 30;
  var observedHeadings = new Set();
  var revealObserver = null;
  var mutationObserver = null;

  function prefersReducedMotion() {
    return window.Axiom && typeof window.Axiom.prefersReducedMotion === 'function'
      ? window.Axiom.prefersReducedMotion()
      : window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isEnabled() {
    return document.body
      && document.body.dataset.headingRevealAnimation === 'true'
      && !prefersReducedMotion();
  }

  function isHeading(element) {
    return element instanceof Element
      && (/^H[1-6]$/.test(element.tagName) || element.hasAttribute('data-heading-reveal'));
  }

  function shouldSkipTextNode(node, heading) {
    var parent = node.parentElement;
    if (!parent || !node.nodeValue || !node.nodeValue.trim()) return true;
    if (parent.closest('svg, script, style, template, .visually-hidden, [aria-hidden="true"], [data-heading-reveal-ignore]')) return true;
    return parent.closest('.' + INNER_CLASS) && heading.contains(parent);
  }

  function getTextNodes(heading) {
    var nodes = [];
    var walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        return shouldSkipTextNode(node, heading) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      }
    });
    var currentNode = walker.nextNode();
    while (currentNode) {
      nodes.push(currentNode);
      currentNode = walker.nextNode();
    }
    return nodes;
  }

  function splitTextNode(node, startIndex) {
    var parts = node.nodeValue.match(/\s+|[^\s]+/g) || [];
    var fragment = document.createDocumentFragment();
    var wordIndex = startIndex;

    parts.forEach(function (part) {
      if (/^\s+$/.test(part)) {
        fragment.appendChild(document.createTextNode(part));
        return;
      }

      var word = document.createElement('span');
      var inner = document.createElement('span');
      word.className = WORD_CLASS;
      inner.className = INNER_CLASS;
      inner.textContent = part;
      inner.style.setProperty('--heading-reveal-delay', (BASE_DELAY + wordIndex * WORD_STAGGER) + 'ms');
      word.appendChild(inner);
      fragment.appendChild(word);
      wordIndex += 1;
    });

    node.replaceWith(fragment);
    return wordIndex;
  }

  function activateHeading(heading, immediate) {
    if (!heading) return;
    if (revealObserver) revealObserver.unobserve(heading);
    observedHeadings.delete(heading);
    heading.dataset.headingRevealState = 'active';

    if (immediate) {
      heading.querySelectorAll('.' + INNER_CLASS).forEach(function (inner) {
        inner.style.animationDelay = '0ms';
        inner.style.animationDuration = '0ms';
      });
    }
  }

  function ensureObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          activateHeading(entry.target, false);
        } else if (entry.boundingClientRect.bottom <= 0) {
          // Already scrolled past, or not rendered (zero rect): show without animating.
          activateHeading(entry.target, true);
        }
      });
    }, {
      rootMargin: '0px 0px -22% 0px',
      threshold: 0
    });
    return revealObserver;
  }

  // No layout reads here: the observer's first callback reports each heading's position
  // after the split words have been laid out, so queuing many headings never forces layout.
  function queueHeading(heading) {
    if (!('IntersectionObserver' in window)) {
      activateHeading(heading, true);
      return;
    }

    heading.dataset.headingRevealState = 'inactive';
    observedHeadings.add(heading);
    ensureObserver().observe(heading);
  }

  function initHeading(heading) {
    if (!isEnabled() || !isHeading(heading) || !heading.closest('.shopify-section')) return;
    if (heading.matches('.visually-hidden, [aria-hidden="true"], [data-heading-reveal-ignore]')) return;
    if (heading.dataset.headingRevealInitialized === 'true' && heading.querySelector('.' + WORD_CLASS)) return;

    var textNodes = getTextNodes(heading);
    if (!textNodes.length) return;

    heading.dataset.headingRevealInitialized = 'true';
    heading.dataset.headingRevealState = 'inactive';
    heading.classList.add('heading-reveal');

    var wordIndex = 0;
    textNodes.forEach(function (node) {
      wordIndex = splitTextNode(node, wordIndex);
    });

    if (!wordIndex) {
      delete heading.dataset.headingRevealInitialized;
      delete heading.dataset.headingRevealState;
      heading.classList.remove('heading-reveal');
      return;
    }

    if (!heading.hasAttribute('data-heading-reveal-manual')) queueHeading(heading);
  }

  function resetHeading(heading) {
    if (!heading) return;
    initHeading(heading);
    if (revealObserver) revealObserver.unobserve(heading);
    observedHeadings.delete(heading);
    heading.dataset.headingRevealState = 'inactive';
    heading.querySelectorAll('.' + INNER_CLASS).forEach(function (inner) {
      inner.style.removeProperty('animation-delay');
      inner.style.removeProperty('animation-duration');
    });
  }

  function collectHeadings(scope) {
    var headings = [];
    if (isHeading(scope) && scope.closest('.shopify-section')) headings.push(scope);
    if (scope && scope.querySelectorAll) {
      scope.querySelectorAll(HEADING_SELECTOR).forEach(function (heading) {
        headings.push(heading);
      });
    }
    return headings;
  }

  function initScope(scope) {
    if (!isEnabled()) return;
    collectHeadings(scope || document).forEach(initHeading);
  }

  function cleanupScope(scope) {
    collectHeadings(scope || document).forEach(function (heading) {
      if (revealObserver) revealObserver.unobserve(heading);
      observedHeadings.delete(heading);
    });
  }

  function handleMutations(mutations) {
    mutations.forEach(function (mutation) {
      mutation.removedNodes.forEach(function (node) {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        cleanupScope(node);
      });

      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        initScope(node);
      });

      if (isHeading(mutation.target) && mutation.target.dataset.headingRevealInitialized === 'true' && !mutation.target.querySelector('.' + WORD_CLASS)) {
        delete mutation.target.dataset.headingRevealInitialized;
        initHeading(mutation.target);
      }
    });
  }

  function revealSelectedBlock(event) {
    collectHeadings(event.target).forEach(function (heading) {
      initHeading(heading);
      activateHeading(heading, true);
    });
  }

  function init() {
    if (!isEnabled()) return;
    initScope(document);
    mutationObserver = new MutationObserver(handleMutations);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', function (event) {
    initScope(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    cleanupScope(event.target);
  });
  document.addEventListener('shopify:block:select', revealSelectedBlock);
  document.addEventListener('axiom:section-rendered', function (event) {
    initScope(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.AxiomHeadingReveal = {
    init: initScope,
    reveal: function (heading) {
      initHeading(heading);
      activateHeading(heading, false);
    },
    reset: resetHeading
  };
})();
